import { UpgradeModel } from "types";

import burstLaserImage from "assets/upgrade-burst-laser.svg";
import darkScopeImage from "assets/upgrade-dark-scope.svg";
import fadeLaserImage from "assets/upgrade-fade-laser.svg";
import firebrandImage from "assets/upgrade-firebrand.svg";
import flashRocketImage from "assets/upgrade-flash-rocket.svg";
import glanceLaserImage from "assets/upgrade-glance-laser.svg";
import glimmerShieldImage from "assets/upgrade-glimmer-shield.svg";
import guardianShieldImage from "assets/upgrade-guardian-shield.svg";
import heatseekerImage from "assets/upgrade-heatseeker.svg";
import ionBarrierImage from "assets/upgrade-ion-barrier.svg";
import kineticaImage from "assets/upgrade-kinetica.svg";
import lightThrusterImage from "assets/upgrade-light-thruster.svg";
import lockSightImage from "assets/upgrade-lock-sight.svg";
import protectorShieldImage from "assets/upgrade-protector-shield.svg";
import pulseLaserImage from "assets/upgrade-pulse-laser.svg";
import quickCoreImage from "assets/upgrade-quick-core.svg";
import sharpScopeImage from "assets/upgrade-sharp-scope.svg";
import shimmerShieldImage from "assets/upgrade-shimmer-shield.svg";
import smallScopeImage from "assets/upgrade-small-scope.svg";
import sparkBoosterImage from "assets/upgrade-spark-booster.svg";
import stormCellImage from "assets/upgrade-storm-cell.svg";
import visionScopeImage from "assets/upgrade-vision-scope.svg";
import waveCellImage from "assets/upgrade-wave-cell.svg";
import zeroCoreImage from "assets/upgrade-zero-core.svg";

import {
  attackAnimation,
  reactorCoreAnimation,
  shieldsAnimation,
  targetingSystemAnimation,
  thrusterAnimation,
} from "components/styles/animations";
import {
  DEFAULT_REACTOR_SPEED,
  DEFAULT_SHIELDS_SPEED,
  DEFAULT_TARGETING_SPEED,
  DEFAULT_THRUSTERS_SPEED,
  DEFAULT_WEAPONS_SPEED,
} from "../constants";

const BISHOP_HOLLOWLANDS_UPGRADES: Record<string, UpgradeModel> = {
  // WEAPONS
  glance_laser: {
    slug: "glance_laser",
    name: "Glance Laser",
    description:
      "A small laser upgrade that increases max damage a little bit. Pew pew.",
    image: glanceLaserImage,
    animation: attackAnimation,
    animationSpeed: DEFAULT_WEAPONS_SPEED,
    part: "weapons",
    requirements: {
      firepower: 3,
    },
    grade: "basic",
    derivedStatsModifiers: {
      maxAttackDamage: 1,
    },
  },
  pulse_laser: {
    slug: "pulse_laser",
    name: "Pulse Laser",
    description: "A laser upgrade that increases maximum attack damage.",
    image: pulseLaserImage,
    animation: attackAnimation,
    animationSpeed: DEFAULT_WEAPONS_SPEED,
    part: "weapons",
    requirements: {
      firepower: 3,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      maxAttackDamage: 1,
    },
  },
  fade_laser: {
    slug: "fade_laser",
    name: "Fade Laser",
    description: "A laser upgrade that weakens opponent ship parts.",
    image: fadeLaserImage,
    animation: attackAnimation,
    animationSpeed: DEFAULT_WEAPONS_SPEED,
    part: "weapons",
    requirements: {
      firepower: 4,
    },
    grade: "basic",
    derivedStatsModifiers: {
      maxWeakenParts: 1,
    },
  },
  burst_laser: {
    slug: "burst_laser",
    name: "Burst Laser",
    description: "A laser upgrade that increases damage.",
    image: burstLaserImage,
    animation: attackAnimation,
    animationSpeed: DEFAULT_WEAPONS_SPEED,
    part: "weapons",
    requirements: {
      firepower: 3,
    },
    grade: "elite",
    derivedStatsModifiers: {
      maxAttackDamage: 1,
    },
  },
  heatseeker: {
    slug: "heatseeker",
    name: "Heatseeker",
    description:
      "A laser that inflicts meaningful damage and weakens ship parts.",
    image: heatseekerImage,
    animation: attackAnimation,
    animationSpeed: DEFAULT_WEAPONS_SPEED,
    part: "weapons",
    requirements: {
      firepower: 8,
    },
    grade: "elite",
    derivedStatsModifiers: {
      maxAttackDamage: 0.5,
      maxWeakenParts: 0.5,
    },
  },
  firebrand: {
    slug: "firebrand",
    name: "Firebrand",
    description: "A strong laser that inflicts meaningful damage.",
    image: firebrandImage,
    animation: attackAnimation,
    animationSpeed: DEFAULT_WEAPONS_SPEED,
    part: "weapons",
    requirements: {
      firepower: 8,
    },
    grade: "ultra",
    derivedStatsModifiers: {
      maxAttackDamage: 0.7,
      maxWeakenParts: 0.3,
    },
  },
  // SHIELDS
  glimmer_shield: {
    slug: "glimmer_shield",
    name: "Glimmer Shield",
    description: "A force field that increases your ship health by a little.",
    image: glimmerShieldImage,
    animation: shieldsAnimation,
    animationSpeed: DEFAULT_SHIELDS_SPEED,
    part: "shields",
    requirements: {
      resilience: 2,
    },
    grade: "basic",
    derivedStatsModifiers: {
      damageReduction: 1,
    },
  },
  shimmer_shield: {
    slug: "shimmer_shield",
    name: "Shimmer Shield",
    description:
      "A force field that increases your ship health and reduces a bit of damage.",
    image: shimmerShieldImage,
    animation: shieldsAnimation,
    animationSpeed: DEFAULT_SHIELDS_SPEED,
    part: "shields",
    requirements: {
      resilience: 3,
    },
    grade: "basic",
    derivedStatsModifiers: {
      maxHealth: 0.5,
      damageReduction: 0.5,
    },
  },
  ion_barrier: {
    slug: "ion_barrier",
    name: "Ion Barrier",
    description: "A force field that increases your ship health.",
    image: ionBarrierImage,
    animation: shieldsAnimation,
    animationSpeed: DEFAULT_SHIELDS_SPEED,
    part: "shields",
    requirements: {
      resilience: 4,
    },
    grade: "basic",
    derivedStatsModifiers: {
      maxHealth: 1,
    },
  },
  protector_shield: {
    slug: "protector_shield",
    name: "Protector Shield",
    description:
      "A decent force field that increases your ship health and damage reduction.",
    image: protectorShieldImage,
    animation: shieldsAnimation,
    animationSpeed: DEFAULT_SHIELDS_SPEED,
    part: "shields",
    requirements: {
      resilience: 8,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      maxHealth: 0.5,
      damageReduction: 0.5,
    },
  },
  guardian_shield: {
    slug: "guardian_shield",
    name: "Guardian Shield",
    description:
      "A force field that increases your ship health and damage reduction.",
    image: guardianShieldImage,
    animation: shieldsAnimation,
    animationSpeed: 800,
    part: "shields",
    requirements: {
      resilience: 8,
    },
    grade: "elite",
    derivedStatsModifiers: {
      maxHealth: 0.6,
      damageReduction: 0.4,
    },
  },
  // THRUSTERS
  spark_booster: {
    slug: "spark_booster",
    name: "Spark Booster",
    description:
      "Small thrusters that increase your attack frequency and ability to dodge hits by a little bit.",
    image: sparkBoosterImage,
    animation: thrusterAnimation,
    animationSpeed: DEFAULT_THRUSTERS_SPEED,
    part: "thrusters",
    requirements: {
      speed: 2,
    },
    grade: "basic",
    derivedStatsModifiers: {
      attackSpeed: 0.5,
      attackEvasion: 0.5,
    },
  },
  light_thruster: {
    slug: "light_thruster",
    name: "Light Thruster",
    description: "Thrusters that increase your ability to dodge hits by a bit.",
    image: lightThrusterImage,
    animation: thrusterAnimation,
    animationSpeed: DEFAULT_THRUSTERS_SPEED,
    part: "thrusters",
    requirements: {
      speed: 2,
    },
    grade: "basic",
    derivedStatsModifiers: {
      attackEvasion: 1,
    },
  },
  flash_rocket: {
    slug: "flash_rocket",
    name: "Flash Rocket",
    description:
      "Thrusters that increase your ability to attack more often and avoid attacks.",
    image: flashRocketImage,
    animation: thrusterAnimation,
    animationSpeed: DEFAULT_THRUSTERS_SPEED,
    part: "thrusters",
    requirements: {
      speed: 7,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      attackSpeed: 0.5,
      attackEvasion: 0.5,
    },
  },
  kinetica: {
    slug: "kinetica",
    name: "Kinetica",
    description:
      "Thrusters that increase your ability to attack more often, and avoid highest damage.",
    image: kineticaImage,
    animation: thrusterAnimation,
    animationSpeed: DEFAULT_THRUSTERS_SPEED,
    part: "thrusters",
    requirements: {
      speed: 7,
    },
    grade: "elite",
    derivedStatsModifiers: {
      attackSpeed: 0.75,
      attackEvasion: 0.25,
    },
  },
  // TARGETING SYSTEMS
  dark_scope: {
    slug: "dark_scope",
    name: "Dark Scope",
    description:
      "A small targeting system upgrade that slightly increases your minimum damage.",
    image: darkScopeImage,
    animation: targetingSystemAnimation,
    animationSpeed: DEFAULT_TARGETING_SPEED,
    part: "targetingSystem",
    requirements: {
      precision: 3,
    },
    grade: "basic",
    derivedStatsModifiers: {
      minAttackDamage: 1,
    },
  },
  small_scope: {
    slug: "small_scope",
    name: "Small Scope",
    description:
      "A small targeting system upgrade that slightly increases your minimum damage and hit chance.",
    image: smallScopeImage,
    animation: targetingSystemAnimation,
    animationSpeed: DEFAULT_TARGETING_SPEED,
    part: "targetingSystem",
    requirements: {
      precision: 3,
    },
    grade: "basic",
    derivedStatsModifiers: {
      attackAccuracy: 0.5,
      minAttackDamage: 0.5,
    },
  },
  vision_scope: {
    slug: "vision_scope",
    name: "Vision Scope",
    description:
      "A targeting system upgrade that increases your minimum damage and hit chance.",
    image: visionScopeImage,
    animation: targetingSystemAnimation,
    animationSpeed: DEFAULT_TARGETING_SPEED,
    part: "targetingSystem",
    requirements: {
      precision: 4,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      attackAccuracy: 0.25,
      minAttackDamage: 0.75,
    },
  },
  lock_sight: {
    slug: "lock_sight",
    name: "Lock Sight",
    description:
      "A targeting system upgrade that increases your minimum damage.",
    image: lockSightImage,
    animation: targetingSystemAnimation,
    animationSpeed: DEFAULT_TARGETING_SPEED,
    part: "targetingSystem",
    requirements: {
      precision: 5,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      minAttackDamage: 1,
    },
  },
  sharp_scope: {
    slug: "sharp_scope",
    name: "Sharp Scope",
    description:
      "A targeting system upgrade that increases the chance of consistently landing hits.",
    image: sharpScopeImage,
    animation: targetingSystemAnimation,
    animationSpeed: DEFAULT_TARGETING_SPEED,
    part: "targetingSystem",
    requirements: {
      precision: 4,
    },
    grade: "elite",
    derivedStatsModifiers: {
      attackAccuracy: 0.8,
      minAttackDamage: 0.2,
    },
  },
  // REACTOR CORES
  zero_core: {
    slug: "zero_core",
    name: "Zero Core",
    description:
      "A small upgrade to your reactor core that allows for slightly higher efficacy of pilot abilities.",
    image: zeroCoreImage,
    animation: reactorCoreAnimation,
    animationSpeed: DEFAULT_REACTOR_SPEED,
    part: "reactorCore",
    requirements: {
      energy: 2,
    },
    grade: "basic",
    derivedStatsModifiers: {
      energyMultiplier: 1,
    },
  },
  quick_core: {
    slug: "quick_core",
    name: "Quick Core",
    description:
      "A small upgrade to your reactor core that allows for slightly more frequent use and higher efficacy of pilot abilities.",
    image: quickCoreImage,
    animation: reactorCoreAnimation,
    animationSpeed: DEFAULT_REACTOR_SPEED,
    part: "reactorCore",
    requirements: {
      energy: 4,
    },
    grade: "basic",
    derivedStatsModifiers: {
      energyMultiplier: 0.5,
      fasterRecharge: 0.5,
    },
  },
  wave_cell: {
    slug: "wave_cell",
    name: "Wave Cell",
    description:
      "An upgrade to your reactor core that strengthens your pilot abilities and allows your abilities to recharge quicker.",
    image: waveCellImage,
    animation: reactorCoreAnimation,
    animationSpeed: DEFAULT_REACTOR_SPEED,
    part: "reactorCore",
    requirements: {
      energy: 5,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      energyMultiplier: 0.5,
      fasterRecharge: 0.5,
    },
  },
  storm_cell: {
    slug: "storm_cell",
    name: "Storm Cell",
    description:
      "A robust upgrade to your reactor core that strengthens your pilot abilities.",
    image: stormCellImage,
    animation: reactorCoreAnimation,
    animationSpeed: DEFAULT_REACTOR_SPEED,
    part: "reactorCore",
    requirements: {
      energy: 5,
    },
    grade: "elite",
    derivedStatsModifiers: {
      energyMultiplier: 1,
    },
  },
};

export default BISHOP_HOLLOWLANDS_UPGRADES;
